import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnInit,
  Provider,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MbsSize, TreeElement, TreeElementDirective } from 'mbs-ui-kit';
import { ExtendForModalTreeElement } from 'mbs-ui-shared@components/tree-in-modal/tree-in-modal.component';
import { GetBackupContentType, MyTreeElements } from 'mbs-ui-shared@models/backup/storages-type';
import { BehaviorSubject, Observable } from 'rxjs';
import { skip } from 'rxjs/operators';
import { ExtendedTreeElement, StepsHelpers, TreeRequestData } from 'wizards/helpers/steps-helpers';
import {
  FilesFoldersData,
  FilesFoldersDataValue,
  FilesFoldersParams,
  FoldersFormData,
  FoldersFormDataValue
} from 'wizards/models/advanced-options-models';
import { TreeIconPath } from 'wizards/models/what-backup-tree-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ParamsForTreeData, WizardStepsService } from 'wizards/services/wizard-steps.service';

import {
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  GroupButtonComponent,
  LoaderComponent,
  SwitcherComponent,
  TextEllipsisComponent,
  TreeComponent
} from 'mbs-ui-kit';

import { FilesFoldersListComponent } from 'wizards/steps/components/files-folders-list/files-folders-list.component';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { NgbCollapseModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

const ViewTabState = {
  tree: '1',
  manually: '2'
};

const FilesFoldersValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FilesFoldersComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-files-folders',
  templateUrl: './files-folders.component.html',
  styleUrls: ['./files-folders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FilesFoldersValueAccessor],
  imports: [
    ButtonComponent,
    CardComponent,
    CardGroupComponent,
    CommonModule,
    FilesFoldersListComponent,
    FormsModule,
    GroupButtonComponent,
    I18NextPipe,
    LoaderComponent,
    NgbTooltipModule,
    ReactiveFormsModule,
    SwitcherComponent,
    TextEllipsisComponent,
    TreeComponent,
    NgbCollapseModule,
    TreeElementDirective
  ]
})
export class FilesFoldersComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  mainService = inject(RemoteManagementWizardsService);
  private stepService = inject(WizardStepsService);

  @ViewChild('fileFoldersListComponent', { read: FilesFoldersListComponent }) fileFoldersListComponent: FilesFoldersListComponent;

  @Input() params: FilesFoldersParams;

  protected readonly MbsSize = MbsSize;

  public readonly viewTabState = ViewTabState;
  private readonly separator = ')-(';
  private readonly paramsForTree: ParamsForTreeData = {
    agentType: 'Backup',
    commandType: 'getComputerContent',
    params: { path: '', offset: 0, limit: 300, isAsync: true, order: 'DisplayNameAsc' }
  };

  public enabledFields = false;
  public firstLoad = true;
  public loading = true;
  public viewMode = ViewTabState.tree;
  public foldersForm: FormGroup<FoldersFormData> = new FormGroup<FoldersFormData>({
    filesFoldersData: new FormControl({ enabledFields: false, paths: [] })
  });

  public treeData: TreeElement[] = [];

  onChange: (value: unknown) => void;
  onTouched: () => void;

  private myValue: FilesFoldersData = null;
  set value(value: FilesFoldersData) {
    this.myValue = value;
    this.notifyValueChange();
  }

  get value(): FilesFoldersData {
    return this.myValue;
  }

  ngOnInit(): void {
    this.initSubscribes();
  }

  updateForm(value: FilesFoldersData): void {
    if (!value) return;

    this.enabledFields = value.enabledFields;
    this.foldersForm.get('filesFoldersData').reset({ enabledFields: value.enabledFields, paths: value.paths || [] });

    if (!this.firstLoad && !this.params?.isRDMode && !this.params?.isOffline) this.initTreeData();
    if (this.params?.isOffline) this.showTreeChangeHandler(ViewTabState.manually);

    this.firstLoad = false;
  }

  updateValidate(): void {
    this.fileFoldersListComponent?.updateValidate();
  }

  enabledFieldsChangeHandler(): void {
    const filesFoldersData = this.foldersForm.get('filesFoldersData');
    const newValue: FilesFoldersDataValue = { enabledFields: this.enabledFields };

    if (this.enabledFields) newValue.paths = filesFoldersData.value.paths || [];

    filesFoldersData.setValue(newValue);

    if (!this.enabledFields) this.treeData.forEach((item: TreeElement) => this.updateTreeItemFromRequest(item));
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  writeValue(value: FilesFoldersData): void {
    this.myValue = value;
    this.updateForm(value);
  }

  registerOnChange(fn: (data: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  showTreeChangeHandler(mode: string): void {
    if (mode === ViewTabState.tree) this.initTreeData();
    else this.paramsForTree.params.path = '';

    this.viewMode = mode;
  }

  getSubtree(root: ExtendForModalTreeElement): Observable<TreeElement[]> {
    const subtree$ = new BehaviorSubject<TreeElement[]>(null);

    this.paramsForTree.params.path = !root.parent ? root.originalPath : (root as any).path;
    this.newSubtreeByParams(subtree$, root);

    return subtree$;
  }

  changeTreeHandler(event: TreeElement): void {
    let childrenShowedCount = 0;
    const str = this.getPathStrFromElement(event);

    if (event.parent) {
      const strParent = this.getPathStrFromElement(event.parent);
      let pathStrings = this.getFilteredPathArray(str, strParent);

      event.children?.forEach((ch) => {
        if (ch.shown) childrenShowedCount++;
        if (ch.disableCheckbox && !event.checked) ch.disableCheckbox = false;
      });
      event.parent?.children?.forEach((c: MyTreeElements) => !c.shown && c.checked && c.path && pathStrings.push(c.path));

      pathStrings = event.checked
        ? this.updatePathStringsIfTreeItemChecked(event, pathStrings, strParent)
        : this.updatePathStringsIfTreeItemNotChecked(event, pathStrings);

      if (event.checked && event.totalChildren > childrenShowedCount) event.children.forEach((ch) => (ch.disableCheckbox = true));

      const formData = this.foldersForm.get('filesFoldersData');

      return void formData.setValue({ ...formData.value, paths: [...new Set(pathStrings)] });
    }

    const pathStrings = this.value.paths?.filter((s) => !s.includes(str) && !str.includes(s));

    event.children?.forEach((ch) => {
      if (ch.shown) childrenShowedCount++;
      if (ch.disableCheckbox && !event.checked) ch.disableCheckbox = false;
    });

    if (event.checked) {
      pathStrings.push(str);
      if (event.totalChildren > childrenShowedCount) event.children.forEach((ch) => (ch.disableCheckbox = true));
    }

    const formData = this.foldersForm.get('filesFoldersData');

    return void formData.setValue({ ...formData.value, paths: [...new Set(pathStrings)] });
  }

  private initSubscribes(): void {
    this.foldersForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (value: FoldersFormDataValue) => (this.value = value.filesFoldersData as FilesFoldersData)
    });

    this.mainService.password.pipe(skip(1), untilDestroyed(this)).subscribe((password: string) => {
      this.paramsForTree.params.Password = password || '';
    });
  }

  private initTreeData(): void {
    this.switchLoading(true);

    this.stepService
      .getRemoteCommandData(this.paramsForTree, this.params.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          if (data.data?.items) {
            data.data.items = StepsHelpers.getSortedTreeItems(data.data.items, !!data.data.items.length && isNaN(+data.data.items[0].type));

            this.setTreeDataFromRequestItems(data.data.items);
            this.treeData.forEach((item: TreeElement) => this.updateTreeItemFromRequest(item));
          } else this.treeData = [];

          this.switchLoading(false);
        },
        error: () => this.switchLoading(false)
      });
  }

  private updateTreeItemFromRequest(item: TreeElement): void {
    const isUnix = this.params.dataForPath.isUnix;
    const joinStr = this.params.dataForPath.join;
    const path = isUnix ? (item as ExtendForModalTreeElement).originalPath : (item.label as string);
    const folders = [...(this.value?.paths || [])];

    if (isUnix && path === joinStr) {
      const newInc = StepsHelpers.findInNotRootFolder(folders, this.treeData, true);
      item.checked = !!newInc.length || folders.some((str) => str === path);
      item.indeterminate = !!newInc.length && !item.checked;

      return;
    }

    const incCurrent = folders.some((incStr) => !StepsHelpers.pathNotIncludePath(isUnix, joinStr, incStr, path));
    item.checked = folders.some((s) => StepsHelpers.equalStrings(path, s, isUnix));
    item.indeterminate = incCurrent && !item.checked;
  }

  private setTreeDataFromRequestItems(items: any[]): void {
    this.treeData = items.map((item, idx) => {
      const pathArray = item.path.split(this.params.dataForPath.join);
      const isAlt = isNaN(+item.type);
      const isUnix = this.params.isUnix;
      const gotChildren = isAlt ? !item.type || item.type === 'File' : +item.type === 3;
      const originalPath = isAlt
        ? pathArray[pathArray.length - 1].toUpperCase() + this.params.dataForPath.join
        : isUnix
          ? item.path
          : item.path.toUpperCase();

      return {
        label:
          isUnix || item.type === GetBackupContentType.Folder || item.type === GetBackupContentType.File ? item.displayName : originalPath,
        originalPath: originalPath,
        id: `disk-${idx}`,
        icon: isAlt
          ? StepsHelpers.getIconFromType(item.type, isAlt)
          : +item.type === 1
            ? TreeIconPath.Folder
            : TreeIconPath[+item.type || 'Disk'],
        gotChildren,
        hideCheckbox: false,
        showNTFSAlert: false,
        checked: false,
        expanded: false,
        shown: true
      };
    });
  }

  private newSubtreeByParams(subtree$: BehaviorSubject<TreeElement[]>, root: MyTreeElements = null): void {
    this.paramsForTree.params.offset = root?.children
      ? root.children.reduce((acc, next) => {
          if (next.shown || next.shown !== false) acc++;
          return acc;
        }, 0)
      : 0;

    this.stepService
      .getRemoteCommandData(this.paramsForTree, this.params.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => subtree$.next(this.getTreeItemsAndUpdateRoot(data, root)),
        error: () => subtree$.next([])
      });
  }

  private getTreeItemsAndUpdateRoot(data: TreeRequestData, root: MyTreeElements): TreeElement[] {
    const settings = {
      root,
      alt: !!data?.data?.items?.length && isNaN(+data.data.items[0].type),
      newInc: [],
      newExc: [],
      isManually: false,
      joinStr: this.params.dataForPath.join,
      sep: this.separator,
      hideFileType: false,
      onlyOneSelect: false,
      isUnix: this.params.isUnix,
      isVirtual: false,
      needHide: null,
      disableCheckbox:
        root.checked &&
        !root.indeterminate &&
        (root.disableCheckbox ||
          (data?.data?.totalCount && data.data.totalCount > data.data.items?.length + this.paramsForTree.params.offset))
    };
    const newResultForm = [...(this.value.paths || [])];

    if (data?.data?.items?.length) data.data.items = StepsHelpers.getSortedTreeItems(data.data.items, settings.alt);

    if (this.paramsForTree?.params?.offset) {
      const pathMap = {};
      data.data.items.forEach((el) => (pathMap[el.restorePath] = true));
      root.children = root.children.filter((ch: MyTreeElements) => ch.shown || !pathMap[ch.path]);
    }

    const treeData = StepsHelpers.generateTreeData(data, newResultForm, [], settings);

    root.totalChildren = StepsHelpers.getTotalChildren(data?.data, treeData.length);

    if (root.children?.[0]?.disableCheckbox && treeData?.length + this.paramsForTree?.params?.offset >= root?.totalChildren) {
      root.children.forEach((el) => (el.disableCheckbox = false));
      treeData.forEach((el) => (el.disableCheckbox = false));
    }

    return treeData;
  }

  private getPathStrFromElement(event: TreeElement): string {
    return this.params.isUnix
      ? (event as ExtendForModalTreeElement).originalPath || (event as ExtendForModalTreeElement).path
      : (event as ExtendForModalTreeElement).path || (event.label as string);
  }

  private getFilteredPathArray(str: string, strParent: string): string[] {
    const strParLen = this.params.dataForPath.join + strParent.length;

    return this.value.paths?.filter((s) => {
      const sLast = s[strParLen];

      if (!s.includes(strParent) || (sLast && sLast !== this.params.dataForPath.join)) return true;

      if (!StepsHelpers.pathNotIncludePath(this.params.isUnix, this.params.dataForPath.join, s, str)) return false;

      const sStartJoinSymbol = s.startsWith(this.params.dataForPath.join);
      const strParentStartJoinSymbol = strParent.startsWith(this.params.dataForPath.join);

      if (sStartJoinSymbol === strParentStartJoinSymbol) {
        return s.split(this.params.dataForPath.join).length - strParent.split(this.params.dataForPath.join).length > 1;
      }

      return sStartJoinSymbol
        ? s.split(this.params.dataForPath.join).length - strParent.split(this.params.dataForPath.join).length > 2
        : s.split(this.params.dataForPath.join).length - strParent.split(this.params.dataForPath.join).length > 0;
    });
  }

  private updatePathStringsIfTreeItemChecked(event: TreeElement, strings: string[], strParent: string): string[] {
    let pathStrings = [...strings];

    if (!event.parent.children.some((c) => c.shown && !c.checked)) {
      const newParentStr = this.updateAllParentItemsChecked(event.parent, strParent);

      pathStrings = pathStrings.filter(
        (s) =>
          StepsHelpers.pathNotIncludePath(this.params.isUnix, this.params.dataForPath.join, s, newParentStr) &&
          StepsHelpers.pathNotIncludePath(this.params.isUnix, this.params.dataForPath.join, s, this.params.dataForPath.join + newParentStr)
      );

      pathStrings.push(newParentStr);

      return pathStrings;
    }

    this.updatePathArrayFromChildren(pathStrings, event.parent.children);

    return pathStrings;
  }

  private updatePathStringsIfTreeItemNotChecked(event: TreeElement, strings: string[]): string[] {
    let pathStrings = [...strings];

    if (!event.parent.indeterminate && !event.parent.checked && event.parent.parent) {
      const indeterminateOrChecked = this.getIndeterminateOrCheckedParent(event.parent, pathStrings);
      const newStr = this.getPathFromItem(indeterminateOrChecked);

      pathStrings = pathStrings.filter((s) => {
        return (
          !StepsHelpers.equalStrings(s, newStr, this.params.isUnix) &&
          StepsHelpers.pathNotIncludePath(this.params.isUnix, this.params.dataForPath.join, newStr, s)
        );
      });

      this.updateAllChildren(indeterminateOrChecked.children, pathStrings);
    }

    if (!event.parent.children.some((c) => c.shown && c.checked && c.id !== event.id)) {
      StepsHelpers.uncheckAllParent(event.parent, true, true);

      return pathStrings;
    }

    pathStrings = this.updateAllParentItems(event.parent, pathStrings);

    return pathStrings;
  }

  private updateAllChildren(children: ExtendedTreeElement[], stringsArray: string[]): void {
    children.forEach((c) => {
      if (c.shown && c.checked) stringsArray.push(this.getPathFromItem(c));
      if (c.children?.length) this.updateAllChildren(c.children, stringsArray);
    });
  }

  private getIndeterminateOrCheckedParent(item: ExtendedTreeElement, stringsArray: string[]): ExtendedTreeElement {
    if (!item.parent) return item;

    const existPath = stringsArray.some((s) => StepsHelpers.equalStrings(s, this.getPathFromItem(item), this.params.isUnix));

    if ((item.indeterminate || !item.checked) && existPath) return item;

    return this.getIndeterminateOrCheckedParent(item.parent, stringsArray);
  }

  private getPathFromItem(item: ExtendedTreeElement): string {
    return (item as ExtendForModalTreeElement).path || (item.label as string);
  }

  private updatePathArrayFromChildren(pathStrings: string[], children: TreeElement[]): void {
    children.forEach(
      (c) =>
        c.shown &&
        c.checked &&
        pathStrings.push(StepsHelpers.getPathInStringFormatByOs(c, this.params.isUnix, false, false, this.params.isUnix, false, true))
    );
  }

  private updateAllParentItemsChecked(parent: TreeElement, strParent = ''): string {
    if (strParent) {
      parent.checked = true;
      parent.indeterminate = false;
    }

    if (parent.parent && !parent.parent.children.some((c) => c.shown && !c.checked)) {
      return this.updateAllParentItemsChecked(parent.parent, this.getPathStrFromElement(parent));
    }

    return strParent;
  }

  private updateAllParentItems(parent: TreeElement, pathStrings: string[]): string[] {
    parent.children.forEach((c) => {
      const shownCheckedAndNotIndeterminate = c.shown && c.checked && (!c.children?.length || !c.indeterminate);

      if (shownCheckedAndNotIndeterminate) {
        pathStrings.push(StepsHelpers.getPathInStringFormatByOs(c, this.params.isUnix, false, false, false, false, true));
      }
    });

    const parentStrLen = (parent.label as string).length + 1;
    const newPathStrings = pathStrings.filter((str) => {
      return parent?.parent
        ? !str.endsWith(parent.label as string) || str[str.length - parentStrLen] !== this.params.dataForPath.join
        : str !== parent.label;
    });

    if (parent?.parent) return this.updateAllParentItems(parent.parent, newPathStrings);

    return newPathStrings;
  }

  private switchLoading(state = true): void {
    this.loading = state;
    this.cdr.detectChanges();
  }
}
